<template>
  <div class="upload-page container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-header-custom d-flex justify-content-between align-items-center mb-3">
          <h2 class="page-title mb-0">Subir Imágenes</h2>
        </div>
      </div>
    </div>

    <div class="row align-items-center">
      <div class="col-md-8 col-12">
        <div class="upload-notice mb-3" role="note">
          <div class="upload-notice-icon">
            <vue-feather type="info" size="22" />
          </div>
          <div class="upload-notice-body">
            <h6 class="upload-notice-title">Requisitos de las imágenes</h6>
            <div class="upload-notice-rules">
              <span class="notice-chip">
                <vue-feather type="image" size="14" />
                Formato JPG
              </span>
              <span class="notice-chip">
                <vue-feather type="hash" size="14" />
                Nombre de 5 a 14 dígitos
              </span>
              <span class="notice-chip">
                <vue-feather type="image" size="14" />
                Dimensiones minimas 1024px X 1024px
              </span>
            </div>
            <p class="upload-notice-examples mb-0">
              Ejemplos:
              <code>12345.jpg</code>
              <code>20240101000123.jpg</code>
            </p>
          </div>
        </div>
      </div>
      <div class="col-md-4 col-12 text-md-end text-start mb-3 mb-md-0">
        <div class="d-flex gap-2 justify-content-md-end justify-content-start flex-wrap">
          <button class="btn btn-cargar" :disabled="!canProcess || uploading" @click="processImages">
            <span v-if="uploading">Subiendo...</span>
            <span v-else>Cargar</span>
            <vue-feather v-if="!uploading" type="upload-cloud" size="18" class="ms-1" />
          </button>
          <button class="btn btn-limpiar" :disabled="!hasFiles || uploading" @click="clearFiles">
            Limpiar
            <vue-feather type="trash-2" size="18" class="ms-1" />
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="dropzone-wrapper" :class="{ 'dropzone-active': isDragActive }" @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
          <div class="dropzone-inner text-center">
            <p class="dropzone-title mb-2">
              Arrastra tus imágenes o una carpeta aquí
            </p>
            <p class="dropzone-subtitle mb-3">
              o selecciona archivos / carpetas desde tu equipo
            </p>

            <div class="dropzone-buttons d-flex justify-content-center gap-2">
              <label class="btn btn-primary">
                Elegir archivos
                <input type="file" accept=".jpg,image/jpg" multiple hidden @change="onFilesSelected" />
              </label>

              <label class="btn btn-outline-primary">
                Elegir carpeta
                <input type="file" accept=".jpg,image/jpg" webkitdirectory hidden @change="onFilesSelected" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay solo durante subida; el procesamiento continúa en segundo plano -->
    <div v-if="uploading" class="upload-overlay">
      <div class="upload-overlay-content">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="visually-hidden">Subiendo...</span>
        </div>
        <p class="mb-2 fw-semibold">Subiendo imágenes ({{ uploadDone }}/{{ uploadTotal }})</p>
        <p class="text-muted small mb-2">El redimensionado se procesará en segundo plano</p>
        <div class="progress w-100" style="max-width: 300px;">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" role="progressbar"
            :style="{ width: uploadProgress + '%' }" :aria-valuenow="uploadProgress" aria-valuemin="0"
            aria-valuemax="100">
            {{ Math.round(uploadProgress) }}%
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-4" v-if="errors.length || validFiles.length">
      <div class="col-md-6" v-if="validFiles.length">
        <h5 class="mb-2">Archivos válidos ({{ validFiles.length }})</h5>
        <ul class="list-group small">
          <li v-for="file in validFiles" :key="file.id"
            class="list-group-item d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-2">
              <img v-if="file.previewUrl" :src="file.previewUrl" alt="preview" class="preview-thumb" />
              <span>{{ file.file.name }}</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="badge bg-success">OK</span>
              <button type="button" class="btn-remove" title="Quitar imagen" :disabled="uploading"
                @click="removeFile(file.id)">
                <vue-feather type="x" size="16" />
              </button>
            </div>
          </li>
        </ul>
      </div>

      <div class="col-md-6" v-if="errors.length">
        <h5 class="mb-2 text-danger">Archivos rechazados ({{ errors.length }})</h5>
        <ul class="list-group small">
          <li v-for="(err, index) in errors" :key="err.id || index"
            class="list-group-item list-group-item-danger d-flex align-items-center justify-content-between gap-2">
            <div class="d-flex align-items-center gap-2">
              <img v-if="err.previewUrl" :src="err.previewUrl" alt="preview" class="preview-thumb preview-thumb-rejected" />
              <div v-else class="preview-thumb preview-placeholder">
                <vue-feather type="file" size="18" />
              </div>
              <span><strong>{{ err.name }}</strong> - {{ err.reason }}</span>
            </div>
            <button type="button" class="btn-remove btn-remove-danger" title="Quitar de la lista" :disabled="uploading"
              @click="removeError(index)">
              <vue-feather type="x" size="16" />
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

let nextId = 1;

export default {
  name: "UploadImages",
  data() {
    return {
      isDragActive: false,
      validFiles: [],
      errors: [],
      uploading: false,
      uploadProgress: 0,
      uploadDone: 0,
      uploadTotal: 0,
    };
  },
  computed: {
    canProcess() {
      return this.validFiles.length > 0;
    },
    hasFiles() {
      return this.validFiles.length > 0 || this.errors.length > 0;
    },
  },
  methods: {
    onDragOver() {
      this.isDragActive = true;
    },
    onDragLeave() {
      this.isDragActive = false;
    },
    async onDrop(event) {
      this.isDragActive = false;
      // Las entradas se deben obtener durante el evento; después el navegador las invalida.
      const entries = Array.from(event.dataTransfer.items || [])
        .map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null))
        .filter(Boolean);
      const files = entries.length
        ? await this.readEntries(entries)
        : Array.from(event.dataTransfer.files || []);
      this.addFiles(files);
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      this.addFiles(files);
    },
    async readEntries(entries) {
      const files = [];
      const visit = async (entry) => {
        if (entry.isFile) {
          files.push(await new Promise((resolve, reject) => entry.file(resolve, reject)));
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          // readEntries devuelve los elementos por lotes; se llama hasta que regrese vacío.
          let batch;
          do {
            batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
            for (const child of batch) await visit(child);
          } while (batch.length);
        }
      };
      for (const entry of entries) await visit(entry);
      return files;
    },
    addFiles(files) {
      if (!files.length) return;
      const { validos, rechazados } = this.handleFiles(files);
      this.showSelectionSummary(validos, rechazados);
    },
    showSelectionSummary(validos, rechazados) {
      const plural = (n, singular, pluralText) => `${n} ${n === 1 ? singular : pluralText}`;
      let icon = "success";
      let title = "Imágenes agregadas";
      let html = `Se agregaron <b>${plural(validos, "imagen válida", "imágenes válidas")}</b> listas para cargar.`;

      if (rechazados > 0 && validos > 0) {
        icon = "warning";
        title = "Selección con archivos rechazados";
        html += `<br><b>${plural(rechazados, "archivo fue rechazado", "archivos fueron rechazados")}</b>; revisa el motivo en la lista.`;
      } else if (rechazados > 0) {
        icon = "error";
        title = "Ningún archivo es válido";
        html = `<b>${plural(rechazados, "archivo fue rechazado", "archivos fueron rechazados")}</b>. Revisa los requisitos de las imágenes y el motivo en la lista.`;
      }

      Swal.fire({ icon, title, html, confirmButtonText: "Aceptar" });
    },
    clearFiles() {
      if (!this.hasFiles || this.uploading) return;
      this.revokePreviews();
      this.revokeErrorPreviews();
      this.validFiles = [];
      this.errors = [];
    },
    removeFile(id) {
      if (this.uploading) return;
      const index = this.validFiles.findIndex((item) => item.id === id);
      if (index === -1) return;
      const [removed] = this.validFiles.splice(index, 1);
      if (removed.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
    },
    removeError(index) {
      if (this.uploading) return;
      const [removed] = this.errors.splice(index, 1);
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
    },
    rejectedEntry(file, reason) {
      const isImage = (file.type || "").startsWith("image/");
      return {
        id: `err-${nextId++}`,
        name: file.name,
        reason,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
      };
    },
    handleFiles(files) {
      const newValid = [];
      const newErrors = [];
      let validos = 0;

      files.forEach((file) => {
        const name = file.name;
        const lower = name.toLowerCase();

        // Debe ser JPG
        if (!lower.endsWith(".jpg") && file.type !== "image/jpg") {
          newErrors.push(this.rejectedEntry(file, "Solo se permiten archivos con extensión .jpg"));
          return;
        }

        // Nombre sin extensión: solo dígitos y entre 5 y 14
        const base = name.replace(/\.jpe?g$/i, "");
        if (!/^\d{5,14}$/.test(base)) {
          newErrors.push(
            this.rejectedEntry(file, "El nombre debe tener entre 5 y 14 dígitos numéricos (sin el .jpg)")
          );
          return;
        }

        validos++;

        // Si ya existe un archivo con el mismo código, lo reemplazamos
        const existingIndex = this.validFiles.findIndex(
          (item) => item.code === base
        );

        const previewUrl = URL.createObjectURL(file);

        if (existingIndex !== -1) {
          // Liberar preview anterior
          const existing = this.validFiles[existingIndex];
          if (existing.previewUrl) {
            URL.revokeObjectURL(existing.previewUrl);
          }

          // Mantener el mismo id para no romper keys en el render
          this.validFiles.splice(existingIndex, 1, {
            id: existing.id,
            file,
            code: base,
            previewUrl,
          });
        } else {
          newValid.push({
            id: nextId++,
            file,
            code: base,
            previewUrl,
          });
        }
      });

      this.validFiles = [...this.validFiles, ...newValid];
      this.errors = [...this.errors, ...newErrors];
      return { validos, rechazados: newErrors.length };
    },
    async processImages() {
      if (!this.canProcess) return;

      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadDone = 0;
      this.uploadTotal = this.validFiles.length;
      this.revokeErrorPreviews();
      this.errors = [];

      const baseUrl =
        process.env.VUE_APP_API_BASE_URL || "http://localhost:3000";
      const CONCURRENT = 5;

      try {
        const resJob = await apiFetch(`${baseUrl}/api/imagenes/jobs`, {
          method: "POST",
        });
        if (!resJob.ok) {
          const errData = await resJob.json().catch(() => ({}));
          throw new Error(errData.message || "No se pudo crear el job");
        }
        const { jobId } = await resJob.json();

        const nuevosErrores = [];
        const items = [...this.validFiles];

        const uploadOne = async (item, index) => {
          const formData = new FormData();
          formData.append("file", item.file);
          const res = await apiFetch(
            `${baseUrl}/api/imagenes/jobs/${jobId}/upload`,
            { method: "POST", body: formData }
          );
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            nuevosErrores.push({
              name: item.file.name,
              reason: data.message || `Error HTTP ${res.status}`,
            });
          }
          this.uploadDone++;
          this.uploadProgress = (this.uploadDone / this.uploadTotal) * 100;
        };

        for (let i = 0; i < items.length; i += CONCURRENT) {
          const batch = items.slice(i, i + CONCURRENT);
          await Promise.all(batch.map((item, j) => uploadOne(item, i + j)));
        }

        this.revokePreviews();
        this.validFiles = [];

        if (nuevosErrores.length) {
          this.errors = nuevosErrores;
          await Swal.fire({
            icon: "warning",
            title: "Carga parcial",
            text: `${nuevosErrores.length} imagen(es) no se pudieron encolar. El resto se procesan en segundo plano.`,
          });
        } else {
          await Swal.fire({
            icon: "success",
            title: "¡Imágenes procesadas correctamente!",
            text: "Las imágenes se están procesando en segundo plano. Puedes seguir trabajando y recibira un correo cuando el proceso finalice.",
          });
        }
      } catch (error) {
        console.error("Error al subir:", error);
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "No se pudo iniciar la carga.",
        });
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
        this.uploadDone = 0;
        this.uploadTotal = 0;
      }
    },
    revokePreviews() {
      this.validFiles.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    },
    revokeErrorPreviews() {
      this.errors.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    },
  },
};
</script>

<style scoped>
.upload-page {
  padding: 20px 15px;
}

.page-title {
  font-weight: 600;
}

.upload-notice {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f7f3ff 100%);
  border: 1px solid #e6dcff;
  border-left: 4px solid #7b5cff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(123, 92, 255, 0.08);
}

.upload-notice-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: rgba(123, 92, 255, 0.12);
  color: #7b5cff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-notice-title {
  margin-bottom: 8px;
  font-weight: 600;
  color: #2b2b2b;
}

.upload-notice-rules {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.notice-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background-color: #ffffff;
  border: 1px solid #ddd0ff;
  color: #5a3fd6;
  font-size: 13px;
  font-weight: 500;
}

.upload-notice-examples {
  font-size: 13px;
  color: #6c757d;
}

.upload-notice-examples code {
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: #f1ecff;
  color: #5a3fd6;
  font-size: 12px;
}

.dropzone-wrapper {
  border: 2px dashed #b38cff;
  border-radius: 12px;
  background-color: #faf5ff;
  padding: 40px 20px;
  transition: all 0.2s ease-in-out;
}

.dropzone-active {
  border-color: #7b5cff;
  background-color: #f2ebff;
}

.dropzone-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #7b5cff;
}

.dropzone-subtitle {
  color: #7a7a9a;
}

.dropzone-buttons .btn {
  min-width: 160px;
}

.btn-remove {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background-color: rgba(248, 31, 88, 0.1);
  color: #f81f58;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.btn-remove:hover:not(:disabled) {
  background-color: #f81f58;
  color: #fff;
}

.btn-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-remove-danger {
  background-color: rgba(255, 255, 255, 0.6);
}

.preview-thumb {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e0e0f0;
}

.preview-thumb-rejected {
  border-color: #f5b5c0;
  background-color: #fff;
}

.preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  border-color: #f5b5c0;
  color: #b02a37;
}

.btn-cargar {
  background-color: #7b5cff;
  border-color: #7b5cff;
  color: white;
}

.btn-cargar:hover:not(:disabled) {
  background-color: #6b4ce6;
  border-color: #6b4ce6;
  color: white;
}

.btn-limpiar {
  background-color: #3fc3ee;
  border-color: #3fc3ee;
  color: white;
}

.btn-limpiar:hover:not(:disabled) {
  background-color: #2ab3de;
  border-color: #2ab3de;
  color: white;
}

.upload-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.upload-overlay-content {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}
</style>
